import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Box,
  InputGroup,
  InputLeftElement,
  Input,
  HStack,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
  Icon,
  Tooltip,
  Divider,
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon, SearchIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { ColumnInfo, Header } from "build-ts/metadata";
import { ColumnType, MetadataTypeEnum } from "build-ts/enums";
import { iconForKgColumn } from "modules/mkg/utils/ontologyEntityIcons";
import { MdBarChart } from "react-icons/md";
import DependenciesModal from "modules/common/DependenciesModal";

/** Returns the Chakra color scheme matching the KG node color for a given column type. */
function colTypeScheme(colType: ColumnType | null | undefined): string {
  if (colType === ColumnType.ATTRIBUTE) return "blue";
  if (colType === ColumnType.MEASURE) return "green";
  if (colType === ColumnType.TEMPORAL) return "yellow";
  return "gray";
}

const KG_STEP1_OPTIONS: { colType: ColumnType; label: string; colorScheme: string }[] = [
  { colType: ColumnType.ATTRIBUTE, label: "Attribute", colorScheme: "blue" },
  { colType: ColumnType.MEASURE, label: "Measure", colorScheme: "green" },
  { colType: ColumnType.TEMPORAL, label: "Datetime", colorScheme: "yellow" },
];

function kgSelectionSubtitle(header: Header): string {
  if (header.column_type === ColumnType.MEASURE) return "Measure";
  if (header.column_type === ColumnType.TEMPORAL) return "Datetime";
  return "Attribute";
}

interface OntologyEntitySelectorProps {
  logicalColumnList: ColumnInfo[];
  kgColumnList: Header[];
  /** All metrics (existing + queued) for this logical column — shown in the metric list dropdown. */
  metricList: Header[];
  /** Currently mapped KG column, or null if unmapped. */
  kgSelection: Header | null;
  columnIndex: number;
  logicalColumn: ColumnInfo;
  dependentKgColumns?: Map<string, Header[]>;
  onKgSelectionChange: (kgColumn: Header | null) => void;
  onOpenCreateKGColumnModal: (columnType: ColumnType, columnIndex: number, defaultName: string) => void;
  onOpenCreateMetricModal: (columnIndex: number, logicalColumn: ColumnInfo) => void;
  autoOpenMenu?: boolean;
  onAfterMenuClose?: (hasSelection: boolean) => void;
  /** When provided, renders a metric card face instead of the KG-column face. Same dropdown opens on click. */
  metricSelection?: Header;
  /** Called when the user requests metric deletion. Returns dep headers on 409, null on success. */
  onDeleteMetricRequest?: (metricId: string) => Promise<Header[] | null>;
}

const OntologyEntitySelector: React.FC<OntologyEntitySelectorProps> = ({
  logicalColumnList,
  kgColumnList,
  metricList,
  kgSelection,
  columnIndex,
  logicalColumn,
  dependentKgColumns,
  onKgSelectionChange,
  onOpenCreateKGColumnModal,
  onOpenCreateMetricModal,
  autoOpenMenu = false,
  onAfterMenuClose,
  metricSelection,
  onDeleteMetricRequest,
}) => {
  const [menuStep, setMenuStep] = useState<1 | 2>(1);
  const [entityKind, setEntityKind] = useState<ColumnType | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [depModalOpen, setDepModalOpen] = useState(false);
  const [depHeaders, setDepHeaders] = useState<Header[]>([]);

  const resetMenu = useCallback(() => {
    setMenuStep(1);
    setEntityKind(null);
    setSearchTerm("");
  }, []);

  const alreadyMappedKgColumns = useMemo(() => {
    if (!dependentKgColumns) return [];
    const mappedColumns: Header[] = [];
    logicalColumnList.forEach((column, index) => {
      if (index === columnIndex) return;
      const kgColumnMapping = dependentKgColumns.get(column.header?.id || "");
      if (kgColumnMapping && kgColumnMapping.length > 0) {
        mappedColumns.push(...kgColumnMapping);
      }
    });
    return mappedColumns;
  }, [dependentKgColumns, logicalColumnList, columnIndex]);

  const filteredKgItems = useMemo(() => {
    if (entityKind === null) return [];
    let list = kgColumnList.filter(h => h.column_type === entityKind);
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter(item => item.name?.toLowerCase().includes(q));
    }
    return list;
  }, [kgColumnList, entityKind, searchTerm]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const suppressParentCloseNotify = useRef(false);
  const autoOpenConsumedRef = useRef(false);

  const handleMenuOpen = () => {
    resetMenu();
    onOpen();
  };

  const handleMenuClose = () => {
    resetMenu();
    onClose();
    if (suppressParentCloseNotify.current) return;
    onAfterMenuClose?.(Boolean(kgSelection));
  };

  useEffect(() => {
    if (!autoOpenMenu) {
      autoOpenConsumedRef.current = false;
      return;
    }
    if (autoOpenConsumedRef.current) return;
    autoOpenConsumedRef.current = true;
    const id = requestAnimationFrame(() => {
      resetMenu();
      onOpen();
    });
    return () => cancelAnimationFrame(id);
  }, [autoOpenMenu, resetMenu, onOpen]);

  const selectAndClose = (kgColumn: Header | null) => {
    suppressParentCloseNotify.current = true;
    onKgSelectionChange(kgColumn);
    resetMenu();
    onClose();
    onAfterMenuClose?.(Boolean(kgColumn));
    suppressParentCloseNotify.current = false;
  };

  const handleDeleteMetricClick = async () => {
    if (!metricSelection?.id || !onDeleteMetricRequest) return;
    suppressParentCloseNotify.current = true;
    resetMenu();
    onClose();
    onAfterMenuClose?.(Boolean(kgSelection));
    requestAnimationFrame(() => {
      suppressParentCloseNotify.current = false;
    });

    setIsDeleting(true);
    try {
      const deps = await onDeleteMetricRequest(metricSelection.id);
      if (deps && deps.length > 0) {
        setDepHeaders(deps);
        setDepModalOpen(true);
      }
      // null return means success
    } finally {
      setIsDeleting(false);
    }
  };

  const kgScheme = colTypeScheme(kgSelection?.column_type);

  return (
    <HStack spacing={2} w="100%" align="stretch">
      {/* Left: KG Column mapping dropdown */}
      <Box flex={1} minW={0}>
        <Menu
          isOpen={isOpen}
          onOpen={handleMenuOpen}
          onClose={handleMenuClose}
          placement="bottom-start"
          closeOnSelect={false}
        >
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            width="100%"
            minW={0}
            overflow="hidden"
            h="auto"
            minH="64px"
            py={2.5}
            px={3}
            bg={metricSelection ? "purple.50" : kgSelection ? `${kgScheme}.50` : "orange.50"}
            borderColor={metricSelection ? "purple.600" : kgSelection ? `${kgScheme}.600` : "orange.600"}
            borderWidth="2px"
            borderStyle={metricSelection || kgSelection ? "solid" : "dashed"}
            _hover={{
              bg: metricSelection ? "purple.100" : kgSelection ? `${kgScheme}.100` : "orange.100",
            }}
            isLoading={isDeleting}
          >
            <HStack w="100%" spacing={2} align="flex-start" justify="space-between">
              <HStack flex={1} minW={0} spacing={2} align="center">
                {metricSelection ? (
                  <>
                    <Icon as={MdBarChart} boxSize={5} color="purple.700" flexShrink={0} aria-hidden />
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Text fontWeight="700" fontSize="sm" noOfLines={2} mb={0} textAlign="left" color="gray.900">
                        {metricSelection.name ?? ""}
                      </Text>
                      <Text fontSize="sm" color="gray.600" mb={0} fontWeight="600">
                        Metric
                      </Text>
                    </VStack>
                  </>
                ) : kgSelection ? (
                  <>
                    <Icon
                      as={iconForKgColumn(kgSelection.column_type)}
                      boxSize={5}
                      color={`${kgScheme}.700`}
                      flexShrink={0}
                      aria-hidden
                    />
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Text fontWeight="700" fontSize="sm" noOfLines={2} mb={0} textAlign="left" color="gray.900">
                        {kgSelection.name}
                      </Text>
                      <Text fontSize="sm" color="gray.600" mb={0} fontWeight="600">
                        {kgSelectionSubtitle(kgSelection)}
                      </Text>
                    </VStack>
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500" mb={0} fontWeight="500" textAlign="left" flex={1}>
                    Select Entity...
                  </Text>
                )}
              </HStack>
              <ChevronDownIcon flexShrink={0} alignSelf="center" />
            </HStack>
          </MenuButton>

          <MenuList
            maxH="500px"
            overflowY="hidden"
            zIndex={1400}
            minW="300px"
            p={0}
            display="flex"
            flexDirection="column"
          >
            {menuStep === 1 ? (
              <Box px={3} py={3} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                <Text fontSize="sm" fontWeight="700" color="gray.500" letterSpacing="wider" mb={2}>
                  ENTITY TYPE
                </Text>
                <Wrap spacing={2}>
                  <WrapItem>
                    <Button
                      size="md"
                      variant="unstyled"
                      borderRadius="lg"
                      bg="purple.50"
                      borderWidth="2px"
                      borderStyle="solid"
                      borderColor="purple.600"
                      color="gray.900"
                      fontWeight="600"
                      px={4}
                      display="flex"
                      alignItems="center"
                      _hover={{ bg: "purple.100" }}
                      leftIcon={<Icon as={MdBarChart} boxSize={5} color="purple.700" />}
                      onClick={() => {
                        onClose();
                        onOpenCreateMetricModal(columnIndex, logicalColumn);
                        requestAnimationFrame(() => {
                          suppressParentCloseNotify.current = false;
                        });
                      }}
                      onPointerDown={() => {
                        suppressParentCloseNotify.current = true;
                      }}
                    >
                      Metric
                    </Button>
                  </WrapItem>
                  {KG_STEP1_OPTIONS.map(opt => (
                    <WrapItem key={opt.colType}>
                      <Button
                        size="md"
                        variant="unstyled"
                        borderRadius="lg"
                        bg={`${opt.colorScheme}.50`}
                        borderWidth="2px"
                        borderStyle="solid"
                        borderColor={`${opt.colorScheme}.600`}
                        color="gray.900"
                        fontWeight="600"
                        px={4}
                        display="flex"
                        alignItems="center"
                        _hover={{ bg: `${opt.colorScheme}.100` }}
                        leftIcon={
                          <Icon as={iconForKgColumn(opt.colType)} boxSize={5} color={`${opt.colorScheme}.700`} />
                        }
                        onClick={() => {
                          setEntityKind(opt.colType);
                          setMenuStep(2);
                          setSearchTerm("");
                        }}
                      >
                        {opt.label}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
                {kgSelection && !metricSelection && (
                  <>
                    <Divider my={3} />
                    <Button size="sm" variant="ghost" colorScheme="red" w="100%" onClick={() => selectAndClose(null)}>
                      Clear Mapping
                    </Button>
                  </>
                )}
                {metricSelection?.id && (
                  <>
                    <Divider my={3} />
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      w="100%"
                      onPointerDown={() => {
                        suppressParentCloseNotify.current = true;
                      }}
                      onClick={handleDeleteMetricClick}
                    >
                      Delete Metric
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" maxH="500px" minH={0} flex={1}>
                <HStack px={2} py={2} borderBottomWidth="1px" borderColor="gray.100" flexShrink={0}>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<ArrowBackIcon boxSize={3} />}
                    onClick={() => {
                      setMenuStep(1);
                      setEntityKind(null);
                      setSearchTerm("");
                    }}
                  >
                    Back
                  </Button>
                  {entityKind !== null && (
                    <HStack spacing={2}>
                      <Icon
                        as={iconForKgColumn(entityKind)}
                        boxSize={4}
                        color={`${colTypeScheme(entityKind)}.700`}
                        aria-hidden
                      />
                      <Text fontSize="sm" fontWeight="600" mb={0} color="gray.700">
                        {KG_STEP1_OPTIONS.find(o => o.colType === entityKind)?.label}
                      </Text>
                    </HStack>
                  )}
                </HStack>

                <MenuItem
                  fontWeight="600"
                  bg="purple.50"
                  color="purple.700"
                  _hover={{ bg: "purple.100" }}
                  onClick={() => {
                    onClose();
                    onOpenCreateKGColumnModal(
                      entityKind ?? ColumnType.ATTRIBUTE,
                      columnIndex,
                      logicalColumn.header?.name || ""
                    );
                  }}
                >
                  <HStack spacing={2} px={2}>
                    <AddIcon />
                    <Text mb={0} fontWeight="700" fontSize="sm">
                      Create New {KG_STEP1_OPTIONS.find(o => o.colType === entityKind)?.label ?? "KG Column"}
                    </Text>
                  </HStack>
                </MenuItem>

                <Box
                  px={3}
                  pt={2}
                  pb={2}
                  flexShrink={0}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                >
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      variant="outline"
                      onMouseDown={e => e.stopPropagation()}
                      onKeyDown={e => e.stopPropagation()}
                      onClick={e => e.stopPropagation()}
                    />
                  </InputGroup>
                </Box>

                <Box flex={1} minH={0} overflowY="auto">
                  {filteredKgItems.map(item => {
                    const isAlreadyMapped =
                      alreadyMappedKgColumns.some(mapped => mapped.id === item.id) && kgSelection?.id !== item.id;

                    return (
                      <Tooltip
                        key={item.id}
                        label="Already mapped to a column"
                        isDisabled={!isAlreadyMapped}
                        hasArrow
                        placement="left"
                        openDelay={200}
                        bg="gray.700"
                        color="white"
                        fontSize="xs"
                      >
                        <Box w="100%" display="block" cursor={isAlreadyMapped ? "not-allowed" : undefined}>
                          <MenuItem
                            aria-disabled={isAlreadyMapped}
                            onClick={() => {
                              if (!isAlreadyMapped) {
                                const h = Header.create({ ...item, type: MetadataTypeEnum.KG_COLUMN });
                                selectAndClose(h);
                              }
                            }}
                            opacity={isAlreadyMapped ? 0.5 : 1}
                            cursor={isAlreadyMapped ? undefined : "pointer"}
                            pointerEvents={isAlreadyMapped ? "none" : undefined}
                            bg={kgSelection?.id === item.id ? "purple.100" : undefined}
                          >
                            <HStack spacing={2}>
                              <Icon as={iconForKgColumn(item.column_type)} boxSize={3.5} flexShrink={0} aria-hidden />
                              <Text mb={0} fontWeight="600">
                                {item.name}
                              </Text>
                            </HStack>
                          </MenuItem>
                        </Box>
                      </Tooltip>
                    );
                  })}

                  {searchTerm && filteredKgItems.length === 0 && <MenuItem isDisabled>No KG columns found</MenuItem>}
                </Box>
              </Box>
            )}
          </MenuList>
        </Menu>
      </Box>

      <DependenciesModal
        isOpen={depModalOpen}
        onClose={() => {
          setDepModalOpen(false);
          setDepHeaders([]);
        }}
        dependencies={depHeaders}
      />
    </HStack>
  );
};

export default OntologyEntitySelector;
